/**
 * Service-area ZIP allowlist for Knox, Blount, and Sevier counties, Tennessee.
 * Includes standard delivery ZIPs and the PO Box / unique codes customers
 * actually write on a form. Overlap ZIPs are included when a meaningful share
 * of the ZIP sits in those three counties (Seymour, Kodak, Rockford, Mascot).
 * Cosby 37722 sits on the Sevier / Cocke line and is included for the Sevier side.
 */

export type County = "Knox" | "Blount" | "Sevier";

export interface ZipInfo {
  city: string;
  county: County;
  note?: string;
}

export const SERVICE_ZIPS: Record<string, ZipInfo> = {
  // Knox County
  "37709": { city: "Corryton", county: "Knox" },
  "37721": { city: "Corryton", county: "Knox" },
  "37754": { city: "Heiskell", county: "Knox" },
  "37779": { city: "Luttrell", county: "Knox", note: "Knox / Union line" },
  "37806": { city: "Mascot", county: "Knox" },
  "37807": { city: "Maynardville", county: "Knox", note: "Knox / Union line" },
  "37849": { city: "Powell", county: "Knox" },
  "37871": { city: "Strawberry Plains", county: "Knox" },
  "37901": { city: "Knoxville", county: "Knox" },
  "37902": { city: "Knoxville", county: "Knox" },
  "37909": { city: "Knoxville", county: "Knox" },
  "37912": { city: "Knoxville", county: "Knox" },
  "37914": { city: "Knoxville", county: "Knox" },
  "37915": { city: "Knoxville", county: "Knox" },
  "37916": { city: "Knoxville", county: "Knox" },
  "37917": { city: "Knoxville", county: "Knox" },
  "37918": { city: "Knoxville", county: "Knox" },
  "37919": { city: "Knoxville", county: "Knox" },
  "37920": { city: "Knoxville", county: "Knox" },
  "37921": { city: "Knoxville", county: "Knox" },
  "37922": { city: "Knoxville", county: "Knox" },
  "37923": { city: "Knoxville", county: "Knox" },
  "37924": { city: "Knoxville", county: "Knox" },
  "37927": { city: "Knoxville", county: "Knox" },
  "37928": { city: "Knoxville", county: "Knox" },
  "37929": { city: "Knoxville", county: "Knox" },
  "37930": { city: "Knoxville", county: "Knox" },
  "37931": { city: "Knoxville", county: "Knox" },
  "37932": { city: "Knoxville", county: "Knox" },
  "37933": { city: "Knoxville", county: "Knox" },
  "37934": { city: "Farragut", county: "Knox" },
  "37938": { city: "Knoxville", county: "Knox" },
  "37939": { city: "Knoxville", county: "Knox" },
  "37940": { city: "Knoxville", county: "Knox" },
  "37950": { city: "Knoxville", county: "Knox" },
  "37995": { city: "Knoxville", county: "Knox" },
  "37996": { city: "Knoxville", county: "Knox" },
  "37997": { city: "Knoxville", county: "Knox" },
  "37998": { city: "Knoxville", county: "Knox" },

  // Blount County
  "37701": { city: "Alcoa", county: "Blount" },
  "37737": { city: "Friendsville", county: "Blount" },
  "37777": { city: "Louisville", county: "Blount" },
  "37801": { city: "Maryville", county: "Blount" },
  "37802": { city: "Maryville", county: "Blount" },
  "37803": { city: "Maryville", county: "Blount" },
  "37804": { city: "Maryville", county: "Blount" },
  "37853": { city: "Rockford", county: "Blount" },
  "37878": { city: "Tallassee", county: "Blount" },
  "37882": { city: "Townsend", county: "Blount" },
  "37886": { city: "Walland", county: "Blount" },

  // Sevier County
  "37722": { city: "Cosby", county: "Sevier", note: "Sevier / Cocke line" },
  "37738": { city: "Gatlinburg", county: "Sevier" },
  "37764": { city: "Kodak", county: "Sevier" },
  "37862": { city: "Sevierville", county: "Sevier" },
  "37863": { city: "Pigeon Forge", county: "Sevier" },
  "37864": { city: "Sevierville", county: "Sevier" },
  "37865": { city: "Seymour", county: "Sevier" },
  "37868": { city: "Pigeon Forge", county: "Sevier" },
  "37876": { city: "Sevierville", county: "Sevier" },
};

export function normalizeZip(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length >= 5) return digits.slice(0, 5);
  return digits;
}

export function lookupZip(raw: string): ZipInfo | null {
  const zip = normalizeZip(raw);
  return SERVICE_ZIPS[zip] ?? null;
}

export function isServedZip(raw: string): boolean {
  return lookupZip(raw) !== null;
}

export const OUT_OF_AREA_MESSAGE =
  "We don't serve your area yet. We're in Knox, Blount, and Sevier counties for now.";
